import { supabase } from './supabase';

/**
 * Review runs and what they found.
 *
 * A run is the unit. The same repository comes back in three months and the question is
 * which of last time's findings are gone — which is a comparison between two runs, not a
 * status somebody remembered to update.
 *
 * See supabase/migrations/20260904020000_cto_reviews.sql.
 */

export const FINDING_CATEGORIES = [
  'security',
  'deliverability',
  'revenue',
  'go-to-market',
  'keeping-the-lights-on',
] as const;
export type FindingCategory = (typeof FINDING_CATEGORIES)[number];

export const CATEGORY_LABEL: Record<FindingCategory, string> = {
  security: 'Security',
  deliverability: 'Deliverability',
  revenue: 'Revenue',
  'go-to-market': 'Go to market',
  'keeping-the-lights-on': 'Keeping the lights on',
};

export function categoryLabel(category: FindingCategory | null): string {
  return category ? CATEGORY_LABEL[category] : 'Uncategorized';
}

/** One sentence across the board: how many open issues sit in each column, and where to start. */
export function findingsNarrative(findings: FindingWithContext[]): string {
  const open = findings.filter((f) => f.status === 'open');
  if (open.length === 0) return '';

  const counts = new Map<FindingCategory | 'uncategorized', number>();
  for (const finding of open) {
    const key = finding.category ?? 'uncategorized';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const parts = ranked.map(([key, n]) => {
    const name = key === 'uncategorized' ? 'uncategorized' : CATEGORY_LABEL[key].toLowerCase();
    return n === 1 ? `1 ${name} issue` : `${n} ${name} issues`;
  });

  const lead = ranked[0];
  const leadName =
    lead[0] === 'uncategorized' ? 'uncategorized' : CATEGORY_LABEL[lead[0]].toLowerCase();

  if (parts.length === 1) {
    return `You've got ${parts[0]}. That's the column to work.`;
  }

  return `You've got ${parts.join(', ')}. Start with ${leadName} — that's the pile that needs a decision.`;
}

export const FINDING_EFFORTS = ['trivial', 'hours', 'days', 'weeks'] as const;
export type FindingEffort = (typeof FINDING_EFFORTS)[number];

export const FINDING_BLAST_RADII = ['one-file', 'one-flow', 'whole-app', 'every-customer'] as const;
export type FindingBlastRadius = (typeof FINDING_BLAST_RADII)[number];

export const FINDING_STATUSES = ['open', 'shipped', 'declined', 'fixed-since', 'stale'] as const;
export type FindingStatus = (typeof FINDING_STATUSES)[number];

export const REVIEW_STATUSES = ['running', 'complete', 'abandoned'] as const;
export type ReviewStatus = (typeof REVIEW_STATUSES)[number];

export interface CtoFindingRow {
  id: string;
  reviewId: string;
  promptId: string | null;
  /** Snapshot, so attribution survives the prompt being renamed or deleted. */
  promptSlug: string | null;
  title: string;
  evidence: string | null;
  detailMd: string | null;
  category: FindingCategory | null;
  effort: FindingEffort | null;
  impact: number | null;
  blastRadius: FindingBlastRadius | null;
  hinge: string | null;
  /** Made the staged list rather than being set aside. */
  staged: boolean;
  position: number;
  status: FindingStatus;
  createdAt: string;
}

export interface CtoReviewRow {
  id: string;
  subscriberId: string | null;
  /** From the embed. Null on a prospect run, where `clientLabel` names the subject instead. */
  subscriberName: string | null;
  subscriberEmail: string | null;
  clientLabel: string | null;
  repo: string;
  repoRef: string | null;
  source: 'manual' | 'mcp';
  status: ReviewStatus;
  summaryMd: string | null;
  reviewedAt: string;
  completedAt: string | null;
  findings: CtoFindingRow[];
}

function mapFinding(r: Record<string, unknown>): CtoFindingRow {
  return {
    id: String(r.id),
    reviewId: String(r.review_id),
    promptId: r.prompt_id ? String(r.prompt_id) : null,
    promptSlug: r.prompt_slug ? String(r.prompt_slug) : null,
    title: String(r.title),
    evidence: r.evidence ? String(r.evidence) : null,
    detailMd: r.detail_md ? String(r.detail_md) : null,
    category: (r.category as FindingCategory) ?? null,
    effort: (r.effort as FindingEffort) ?? null,
    impact: r.impact === null || r.impact === undefined ? null : Number(r.impact),
    blastRadius: (r.blast_radius as FindingBlastRadius) ?? null,
    hinge: r.hinge ? String(r.hinge) : null,
    staged: Boolean(r.staged),
    position: Number(r.position ?? 0),
    status: (r.status as FindingStatus) ?? 'open',
    createdAt: String(r.created_at),
  };
}

function mapReview(r: Record<string, unknown>): CtoReviewRow {
  const findings = Array.isArray(r.cto_findings)
    ? (r.cto_findings as Record<string, unknown>[]).map(mapFinding)
    : [];
  // Staged first in their intended order, then everything set aside by impact. The report
  // order is the point of `position`; the rest is a pile to skim.
  findings.sort((a, b) => {
    if (a.staged !== b.staged) return a.staged ? -1 : 1;
    if (a.staged) return a.position - b.position;
    return (b.impact ?? 0) - (a.impact ?? 0);
  });

  const subscriber = (r.subscriber ?? null) as { name?: string; email?: string } | null;

  return {
    id: String(r.id),
    subscriberId: r.subscriber_id ? String(r.subscriber_id) : null,
    subscriberName: subscriber?.name ?? null,
    subscriberEmail: subscriber?.email ?? null,
    clientLabel: r.client_label ? String(r.client_label) : null,
    repo: String(r.repo),
    repoRef: r.repo_ref ? String(r.repo_ref) : null,
    source: (r.source as 'manual' | 'mcp') ?? 'manual',
    status: (r.status as ReviewStatus) ?? 'running',
    summaryMd: r.summary_md ? String(r.summary_md) : null,
    reviewedAt: String(r.reviewed_at),
    completedAt: r.completed_at ? String(r.completed_at) : null,
    findings,
  };
}

const SELECT = '*, subscriber:subscribers(name, email), cto_findings(*)';

/** Who the run was for, however the row happens to say it. */
export function reviewSubject(review: CtoReviewRow): string {
  return review.subscriberName ?? review.subscriberEmail ?? review.clientLabel ?? 'Unattributed';
}

/**
 * Everything collected, newest first, regardless of who it was for.
 *
 * Capped rather than paginated: at the volume where this stops being enough, the page needs
 * a real query interface anyway, and a limit that shows is better than one that silently
 * truncates.
 */
export async function fetchAllReviews(limit = 200): Promise<CtoReviewRow[]> {
  const { data, error } = await supabase
    .from('cto_reviews')
    .select(SELECT)
    .order('reviewed_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapReview(r as Record<string, unknown>));
}

export async function fetchReviewsForSubscriber(subscriberId: string): Promise<CtoReviewRow[]> {
  const { data, error } = await supabase
    .from('cto_reviews')
    .select(SELECT)
    .eq('subscriber_id', subscriberId)
    .order('reviewed_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => mapReview(r as Record<string, unknown>));
}

export async function createReview(input: {
  subscriberId?: string | null;
  clientLabel?: string | null;
  repo: string;
  repoRef?: string | null;
  source?: 'manual' | 'mcp';
}): Promise<CtoReviewRow> {
  const { data, error } = await supabase
    .from('cto_reviews')
    .insert({
      subscriber_id: input.subscriberId ?? null,
      client_label: input.clientLabel ?? null,
      repo: input.repo.trim(),
      repo_ref: input.repoRef?.trim() || null,
      source: input.source ?? 'manual',
    })
    .select(SELECT)
    .single();
  if (error) throw new Error(error.message);
  return mapReview(data as Record<string, unknown>);
}

export async function updateReview(
  id: string,
  patch: { status?: ReviewStatus; summaryMd?: string | null; repoRef?: string | null },
): Promise<void> {
  const payload: Record<string, unknown> = {};
  if (patch.status !== undefined) {
    payload.status = patch.status;
    // Recording when a run finished is what makes "reviewed three months ago" answerable.
    payload.completed_at = patch.status === 'complete' ? new Date().toISOString() : null;
  }
  if (patch.summaryMd !== undefined) payload.summary_md = patch.summaryMd;
  if (patch.repoRef !== undefined) payload.repo_ref = patch.repoRef;

  const { error } = await supabase.from('cto_reviews').update(payload).eq('id', id);
  if (error) throw new Error(error.message);
}

export async function deleteReview(id: string): Promise<void> {
  const { error } = await supabase.from('cto_reviews').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

export async function updateFindingStatus(id: string, status: FindingStatus): Promise<void> {
  const { error } = await supabase.from('cto_findings').update({ status }).eq('id', id);
  if (error) throw new Error(error.message);
}

/** A finding carrying the run it came from, so it can be listed away from that run. */
export interface FindingWithContext extends CtoFindingRow {
  repo: string;
  repoRef: string | null;
  reviewedAt: string;
  reviewStatus: ReviewStatus;
  /** Carried so a findings list can be read without the run it belongs to. */
  subject: string;
  subscriberId: string | null;
}

export function flattenFindings(reviews: CtoReviewRow[]): FindingWithContext[] {
  return reviews.flatMap((r) =>
    r.findings.map((f) => ({
      ...f,
      repo: r.repo,
      repoRef: r.repoRef,
      reviewedAt: r.reviewedAt,
      reviewStatus: r.status,
      subject: reviewSubject(r),
      subscriberId: r.subscriberId,
    })),
  );
}

/** Cheapest first when sorting by effort — the useful direction, not alphabetical. */
export const EFFORT_RANK: Record<FindingEffort, number> = {
  trivial: 0,
  hours: 1,
  days: 2,
  weeks: 3,
};

/** Hours and trivial are an afternoon. Days and weeks are a project. */
export function effortIsLow(effort: FindingEffort | null): boolean | null {
  if (!effort) return null;
  return effort === 'trivial' || effort === 'hours';
}

/** Four and five are the ones worth rearranging a week for. */
export function impactIsHigh(impact: number | null): boolean | null {
  if (impact == null) return null;
  return impact >= 4;
}

export type FindingQuadrant = 'focus' | 'schedule' | 'fill' | 'skip' | 'unscored';

export function findingQuadrant(finding: {
  effort: FindingEffort | null;
  impact: number | null;
}): FindingQuadrant {
  const lowEffort = effortIsLow(finding.effort);
  const highImpact = impactIsHigh(finding.impact);
  if (lowEffort == null || highImpact == null) return 'unscored';
  if (lowEffort && highImpact) return 'focus';
  if (!lowEffort && highImpact) return 'schedule';
  if (lowEffort && !highImpact) return 'fill';
  return 'skip';
}

export type ParsedRepoRemote = {
  host: 'github' | 'other';
  /** org/repo, or the original string if we cannot split it. */
  path: string;
  /** github.com/org/repo — no protocol, no .git */
  fullPath: string;
  title: string;
};

function stripGitSuffix(value: string): string {
  return value.replace(/\.git$/i, '');
}

/** Turn a stored remote into a short label and a hover that still has the whole path. */
export function parseRepoRemote(raw: string): ParsedRepoRemote {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { host: 'other', path: raw, fullPath: raw, title: raw };
  }

  const ssh = trimmed.match(/^git@([^:]+):(.+)$/i);
  if (ssh) {
    return describeRepoHost(ssh[1], stripGitSuffix(ssh[2]));
  }

  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
  const slash = withoutProtocol.indexOf('/');
  if (slash > 0) {
    const host = withoutProtocol.slice(0, slash);
    const path = stripGitSuffix(withoutProtocol.slice(slash + 1).replace(/^\/+/, ''));
    if (host.includes('.')) {
      return describeRepoHost(host, path);
    }
  }

  return {
    host: 'other',
    path: stripGitSuffix(trimmed),
    fullPath: stripGitSuffix(trimmed),
    title: trimmed,
  };
}

function describeRepoHost(host: string, path: string): ParsedRepoRemote {
  const hostName = host.toLowerCase();
  const fullPath = `${hostName}/${path}`;
  if (hostName === 'github.com' || hostName === 'www.github.com') {
    return {
      host: 'github',
      path,
      fullPath: `github.com/${path}`,
      title: `GitHub repo — github.com/${path}`,
    };
  }
  return {
    host: 'other',
    path,
    fullPath,
    title: fullPath,
  };
}

export interface RepoSummary {
  repo: string;
  runs: number;
  lastReviewedAt: string;
  openFindings: number;
  totalFindings: number;
  hasKnown: boolean;
  lastKnownAt: string | null;
}

/**
 * One person can hand you three repositories. Rolling up by repo is what makes that visible
 * before you start reading individual findings. A known without a review still belongs here —
 * we already know the remote exists.
 */
export function summarizeRepos(
  reviews: CtoReviewRow[],
  knowns: { repo: string; updatedAt: string }[] = [],
): RepoSummary[] {
  const byRepo = new Map<string, RepoSummary>();
  for (const r of reviews) {
    const existing = byRepo.get(r.repo);
    const open = r.findings.filter((f) => f.status === 'open').length;
    if (existing) {
      existing.runs += 1;
      existing.openFindings += open;
      existing.totalFindings += r.findings.length;
      if (r.reviewedAt > existing.lastReviewedAt) existing.lastReviewedAt = r.reviewedAt;
    } else {
      byRepo.set(r.repo, {
        repo: r.repo,
        runs: 1,
        lastReviewedAt: r.reviewedAt,
        openFindings: open,
        totalFindings: r.findings.length,
        hasKnown: false,
        lastKnownAt: null,
      });
    }
  }
  for (const known of knowns) {
    const existing = byRepo.get(known.repo);
    if (existing) {
      existing.hasKnown = true;
      if (!existing.lastKnownAt || known.updatedAt > existing.lastKnownAt) {
        existing.lastKnownAt = known.updatedAt;
      }
    } else {
      byRepo.set(known.repo, {
        repo: known.repo,
        runs: 0,
        lastReviewedAt: known.updatedAt,
        openFindings: 0,
        totalFindings: 0,
        hasKnown: true,
        lastKnownAt: known.updatedAt,
      });
    }
  }
  return [...byRepo.values()].sort((a, b) => {
    const aAt = a.lastKnownAt && a.lastKnownAt > a.lastReviewedAt ? a.lastKnownAt : a.lastReviewedAt;
    const bAt = b.lastKnownAt && b.lastKnownAt > b.lastReviewedAt ? b.lastKnownAt : b.lastReviewedAt;
    return bAt.localeCompare(aAt);
  });
}

/**
 * What a re-run is told about the last one.
 *
 * Titles and status only. Sending the full findings back would have the model agree with
 * itself — the point is to let it say "gone" or "still there", not to remind it what it
 * concluded and why.
 */
export function priorFindingsBrief(reviews: CtoReviewRow[], repo?: string): string {
  // Scoped to one repository. A member with three of them gets three separate briefs —
  // handing a session findings from a different codebase is worse than handing it none.
  const scoped = repo ? reviews.filter((r) => r.repo === repo) : reviews;
  const last = scoped.find((r) => r.status === 'complete') ?? scoped[0];
  if (!last) return '';
  const lines = last.findings
    .filter((f) => f.status === 'open' || f.status === 'shipped')
    .map((f) => `- ${f.title} (${f.status}, ${f.promptSlug ?? 'unattributed'})`);
  if (lines.length === 0) return '';
  return [
    `Previously found in ${last.repo} on ${new Date(last.reviewedAt).toLocaleDateString()}:`,
    ...lines,
    '',
    'For each, say whether it is fixed, still present, or you cannot tell. Do not re-argue',
    'the ones that are still present — report new findings only.',
  ].join('\n');
}

export const CATEGORY_STYLE: Record<FindingCategory, string> = {
  security: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  deliverability: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
  revenue: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  'go-to-market': 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  'keeping-the-lights-on': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export const CATEGORY_HINT: Record<FindingCategory, string> = {
  security:
    'If this is wrong, someone else has the keys. Fix it before you ship the next thing.',
  deliverability: 'Mail that does not arrive is a product that does not exist.',
  revenue: 'Money is in the path. Do this now or the week stays quiet for the wrong reason.',
  'go-to-market': 'This is how people find you. Leaving it unread is deciding on instinct.',
  'keeping-the-lights-on':
    'This is super critical. Do this now because it helps keep the lights on.',
};

export const BLAST_HINT: Record<FindingBlastRadius, string> = {
  'one-file': 'Contained. One place to change.',
  'one-flow': 'One path people walk — sign-in, checkout, setup.',
  'whole-app': 'The whole product feels this. Treat it as shared, not a local tweak.',
  'every-customer': 'Every customer feels this the next time they show up.',
};

export const EFFORT_HINT: Record<FindingEffort, string> = {
  trivial: 'Under an hour. A config line or a deletion.',
  hours: 'An afternoon. One file, no new concepts.',
  days: 'A few days. Touches several places or needs a decision first.',
  weeks: 'A project. Probably should not start this month.',
};

/**
 * Keep an open Reviews page current. Realtime on the run and its findings, plus a
 * quiet poll while the tab is visible — same shape as watching member activity.
 */
export function subscribeCtoReviewsLive(
  onChange: () => void,
  opts?: { subscriberId?: string; pollMs?: number },
): () => void {
  const subscriberId = opts?.subscriberId;
  let debounce: number | null = null;
  const kick = () => {
    if (debounce != null) window.clearTimeout(debounce);
    debounce = window.setTimeout(() => {
      debounce = null;
      onChange();
    }, 200);
  };

  const reviewFilter = subscriberId
    ? {
        event: '*' as const,
        schema: 'public',
        table: 'cto_reviews',
        filter: `subscriber_id=eq.${subscriberId}`,
      }
    : { event: '*' as const, schema: 'public', table: 'cto_reviews' };

  const knownFilter = subscriberId
    ? {
        event: '*' as const,
        schema: 'public',
        table: 'cto_repo_knowns',
        filter: `subscriber_id=eq.${subscriberId}`,
      }
    : { event: '*' as const, schema: 'public', table: 'cto_repo_knowns' };

  const channel = supabase
    .channel(subscriberId ? `cto_reviews:member:${subscriberId}` : 'cto_reviews:all')
    .on('postgres_changes', reviewFilter, kick)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'cto_findings' },
      kick,
    )
    .on('postgres_changes', knownFilter, kick)
    .subscribe();

  const tick = () => {
    if (document.hidden) return;
    kick();
  };
  const interval = window.setInterval(tick, opts?.pollMs ?? 8_000);
  document.addEventListener('visibilitychange', tick);

  return () => {
    if (debounce != null) window.clearTimeout(debounce);
    window.clearInterval(interval);
    document.removeEventListener('visibilitychange', tick);
    void supabase.removeChannel(channel);
  };
}
